
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Eye,
  Heart,
  Calendar,
  BarChart3,
  TrendingUp,
  Filter,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

interface ServiceAnalyticsData {
  service_id: string;
  service_name: string;
  likes: number;
  appointments: number;
}

export const ServiceAnalytics = () => {
  const [serviceStats, setServiceStats] = useState<ServiceAnalyticsData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("7days");
  const [chartData, setChartData] = useState<any[]>([]);

  const fetchServiceAnalytics = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      // First, get all services by this professional
      const { data: services, error: servicesError } = await supabase
        .from("professional_services")
        .select("id, service_name")
        .eq("professional_id", user.id);

      if (servicesError) throw servicesError;
      
      if (!services || services.length === 0) {
        setServiceStats([]);
        setChartData([]);
        setIsLoading(false);
        return;
      }

      // Calculate date range based on timeframe
      const now = new Date();
      let startDate = new Date();
      
      switch(timeframe) {
        case "24hours":
          startDate.setDate(now.getDate() - 1);
          break;
        case "7days":
          startDate.setDate(now.getDate() - 7);
          break;
        case "30days":
          startDate.setDate(now.getDate() - 30);
          break;
        case "alltime":
          startDate = new Date(2000, 0, 1); // Far in the past
          break;
      }

      // For each service, get analytics
      const analyticsPromises = services.map(async (service) => {
        // Get likes count
        const { count: likeCount, error: likesError } = await supabase
          .from("favorites")
          .select("*", { count: 'exact', head: true })
          .eq("service_id", service.id)
          .gte("created_at", startDate.toISOString());
          
        if (likesError) {
          console.error("Error fetching likes for service:", likesError);
        }

        // Get appointments 
        const { data: appointmentsData, error: appointmentsError } = await supabase
          .from("professional_appointments")
          .select("*")
          .eq("service_id", service.id)
          .gte("created_at", startDate.toISOString());

        if (appointmentsError) {
          console.error("Error fetching appointments:", appointmentsError);
        }

        return {
          service_id: service.id,
          service_name: service.service_name,
          likes: likeCount || 0,
          appointments: appointmentsData?.length || 0,
        };
      });

      const analyticsResults = await Promise.all(analyticsPromises);
      
      // Create chart data
      const chartData = analyticsResults.map(service => ({
        name: service.service_name,
        likes: service.likes,
        appointments: service.appointments
      }));
      
      setChartData(chartData);
      setServiceStats(analyticsResults);
    } catch (error) {
      console.error("Error fetching service analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceAnalytics();
  }, [timeframe]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Statistiques des services</h2>
        <div className="flex gap-2">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24hours">24 heures</SelectItem>
              <SelectItem value="7days">7 jours</SelectItem>
              <SelectItem value="30days">30 jours</SelectItem>
              <SelectItem value="alltime">Tout</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchServiceAnalytics}>
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : serviceStats.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-48">
            <BarChart3 className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">Aucun service ou donnée analytique disponible</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Statistiques par service</CardTitle>
              <CardDescription>Comparaison des likes et rendez-vous</CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12 }}
                      height={60}
                      tickMargin={10}
                      angle={-45}
                      textAnchor="end"
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar name="Likes" dataKey="likes" fill="#ef4444" />
                    <Bar name="Rendez-vous" dataKey="appointments" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {serviceStats.map((stats) => (
              <Card key={stats.service_id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg truncate">{stats.service_name}</CardTitle>
                  <CardDescription className="flex items-center">
                    <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                    Activité de la période
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="bg-red-100 p-2 rounded-full mr-3">
                          <Heart className="h-4 w-4 text-red-600" />
                        </div>
                        <span className="text-sm">Likes</span>
                      </div>
                      <span className="font-medium">{stats.likes}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="bg-blue-100 p-2 rounded-full mr-3">
                          <Calendar className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="text-sm">Rendez-vous</span>
                      </div>
                      <span className="font-medium">{stats.appointments}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
